import * as React from 'react';

import { useAtom, useSetAtom } from 'jotai';

import { CreateQuizEventAdapter } from '~analytics/adapters/CreateQuizEventAdapter';
import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';
import {
  CurrentQuizEditorDialog,
  currentQuizEditorDialogAtom,
} from '~teacher/components/QuizEditor/atoms';
import { positionAtom as generateQuestionDialogPositionAtom } from '~teacher/components/QuizEditor/GenerateQuestionDialog/atom';
import { positionAtom as importFromTextPositionAtom } from '~teacher/components/QuizEditor/ImportFromTextDialog';
import { navigateByQuizId } from '~teacher/components/workspaces/WorkspaceQuizzes/utils/navigateByItemType';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { useIsWorkspaceReadOnly } from '~teacher/hook/workspaces/settings';
import { useCreateWorkspaceQuiz } from '~teacher/hook/workspaces/useCreateWorkspaceQuiz';

interface UseCreateQuizParams {
  withAi?: boolean;
  workspaceId: string;
  parentFolderId: string;
}

export function useCreateQuiz({
  workspaceId,
  parentFolderId,
}: UseCreateQuizParams) {
  const {
    permissions: { createQuiz: hasCreateQuizPermission },
  } = useWorkspacePermissionsV2({ workspaceId });
  // Official (read-only) libraries lock content creation for everyone.
  const isReadOnly = useIsWorkspaceReadOnly(workspaceId);
  const canCreateQuiz = hasCreateQuizPermission && !isReadOnly;
  const setCurrentQuizEditorDialogAtom = useSetAtom(
    currentQuizEditorDialogAtom
  );

  const createNewWorkspaceQuizMutation = useCreateWorkspaceQuiz();
  const socrativeRouter = useSocrativeRouter();
  const [isCreatingQuiz, setIsCreatingQuiz] = React.useState(false);

  const [, setGenerateQuestionPosition] = useAtom(
    generateQuestionDialogPositionAtom
  );

  const [, setImportFromTextPosition] = useAtom(importFromTextPositionAtom);

  const createQuiz = React.useCallback(
    (options: { aiFeature?: 'generate' | 'import' | 'extract' }) => {
      if (!canCreateQuiz) return; // cannot create quizzes, so call back does nothing.

      setIsCreatingQuiz(true);

      createNewWorkspaceQuizMutation.mutate(
        {
          workspaceId: workspaceId,
          workspaceQuizName: translate('Untitled Quiz'),
          parentFolderId: parentFolderId,
        },
        {
          onSuccess: async ({
            data: {
              workspaceQuiz: { workspaceId, id: workspaceQuizId },
            },
          }) => {
            navigateByQuizId({
              workspaceId: workspaceId,
              workspaceQuizId: workspaceQuizId,
              router: socrativeRouter,
            });

            if (options.aiFeature === 'generate') {
              setCurrentQuizEditorDialogAtom(
                CurrentQuizEditorDialog.GenerateQuestions
              );
              setGenerateQuestionPosition(1);
            } else if (options.aiFeature === 'import') {
              setCurrentQuizEditorDialogAtom(
                CurrentQuizEditorDialog.ImportFromText
              );
              setImportFromTextPosition(1);
            } else if (options.aiFeature === 'extract') {
              setCurrentQuizEditorDialogAtom(
                CurrentQuizEditorDialog.ExtractQuestionsFromDoc
              );
            } else {
              setCurrentQuizEditorDialogAtom(CurrentQuizEditorDialog.None);
            }

            if (!options?.aiFeature) {
              new CreateQuizEventAdapter({
                creation_method: 'app',
                source_workspace_id: workspaceId,
                destination_workspace_id: workspaceId,
              }).sendAnalytics();
            } else {
              new CreateQuizEventAdapter({
                creation_method:
                  options.aiFeature === 'generate'
                    ? 'create_from_ai'
                    : 'create_from_ai_import',
                source_workspace_id: workspaceId,
                destination_workspace_id: workspaceId,
              }).sendAnalytics();
            }
            setIsCreatingQuiz(false);
          },
          onError: () => {
            toast.critical('There was an error creating your quiz');
            setIsCreatingQuiz(false);
          },
        }
      );
    },
    [
      canCreateQuiz,
      createNewWorkspaceQuizMutation,
      workspaceId,
      parentFolderId,
      socrativeRouter,
      setCurrentQuizEditorDialogAtom,
      setGenerateQuestionPosition,
      setImportFromTextPosition,
    ]
  );

  return {
    createQuiz,
    createNewWorkspaceQuizMutation,
    isCreatingQuiz,
    canCreateQuiz,
  };
}
